import Vue from "vue";
import Router from "vue-router";

// Containers
const DefaultContainer = () => import("@/containers/DefaultContainer");

// Views
const Dashboard = () => import("@/views/Dashboard");

// Example
const stackedChart = () => import("@/components/chart/stackedChart");

const Colors = () => import("@/views/theme/Colors");
const Typography = () => import("@/views/theme/Typography");

const Charts = () => import("@/views/Charts");
const Widgets = () => import("@/views/Widgets");

// Views - Components
const Cards = () => import("@/views/base/Cards");
const Forms = () => import("@/views/base/AddNewItem");
const Switches = () => import("@/views/base/Switches");
const Tables = () => import("@/views/base/Tables");
const MapByState = () => import("@/views/base/MapState");
const Tabs = () => import("@/views/base/Tabs");
const Breadcrumbs = () => import("@/views/base/Breadcrumbs");
const Carousels = () => import("@/views/base/Carousels");
const Collapses = () => import("@/views/base/Collapses");
const Jumbotrons = () => import("@/views/base/Jumbotrons");
const ListGroups = () => import("@/views/base/ListGroups");
const Navs = () => import("@/views/base/Navs");
const Navbars = () => import("@/views/base/Navbars");
const Paginations = () => import("@/views/base/Paginations");
const Popovers = () => import("@/views/base/Popovers");
const ProgressBars = () => import("@/views/base/ProgressBars");
const Tooltips = () => import("@/views/base/Tooltips");
const OEM = () => import("@/views/base/OemRepair.vue");
const BER = () => import("@/views/base/ber.vue");
const EOL = () => import("@/views/base/eol.vue");
const NTF = () => import("@/views/base/ntf.vue");
const WAR = () => import("@/views/base/war.vue");
const TAT = () => import("@/views/base/tat.vue");
const Inventory = () => import("@/views/base/inventory.vue");
const Backlog = () => import("@/views/base/ActiveBacklog.vue");
const Map = () => import("@/views/base/Map.vue");
const SalesMap = () => import("@/views/base/SalesMap.vue");
const SalesRep = () => import("@/views/base/SalesRep.vue");
const City = () => import("@/views/base/city.vue");
const SubDivision = () => import("@/views/base/SubDivision.vue");
const Reports = () => import("@/views/base/Reports.vue");
const ReportsFin = () => import("@/views/base/ReportsFin.vue");

const AnalyticsByModel = () => import("@/views/base/AnalyticsByModel.vue");
const ModelView = () => import("@/views/base/Model");

const BacklogByLocation = () => import("@/views/base/BacklogByLocation");

const Profile = () => import("@/views/base/ProfileSettings.vue");

// Views - Buttons
const StandardButtons = () => import("@/views/buttons/StandardButtons");
const ButtonGroups = () => import("@/views/buttons/ButtonGroups");
const Dropdowns = () => import("@/views/buttons/Dropdowns");
const BrandButtons = () => import("@/views/buttons/BrandButtons");

// Views - Icons
const Flags = () => import("@/views/icons/Flags");
const FontAwesome = () => import("@/views/icons/FontAwesome");
const SimpleLineIcons = () => import("@/views/icons/SimpleLineIcons");
const CoreUIIcons = () => import("@/views/icons/CoreUIIcons");

// Views - Notifications
const Alerts = () => import("@/views/notifications/Alerts");
const Badges = () => import("@/views/notifications/Badges");
const Modals = () => import("@/views/notifications/Modals");

// Views - Pages
const Page404 = () => import("@/views/pages/Page404");
const Page500 = () => import("@/views/pages/Page500");
const Login = () => import("@/views/pages/Login");
const Register = () => import("@/views/pages/Register");

// Users
const Users = () => import("@/views/users/Users");
const User = () => import("@/views/users/User");

// Proof Of Delivery
const ProofOfDelivery = () => import("@/views/base/ProofOfDelivery");

// Table All Data
const TableAllData = () => import("@/views/base/TableAllData.vue");
const TableDeliveredPerCustomer = () =>
  import("@/views/base/TableDeliveredPerCustomer.vue");

// CompanyProfile
const CompanyProfile = () => import("@/views/base/CompanyProfile.vue");

// Issues
const IssuePreview = () => import("@/views/base/IssuePreview.vue");
const IssueMap = () => import("@/views/base/IssueMap.vue");
const IssueProfile = () => import("@/views/base/IssueProfile.vue");
const FailureDetails = () => import("@/views/base/FailureDetailsTable.vue");
const WaitingForParts = () => import("@/views/base/WaitingForParts.vue");
const DailyReceiving = () => import("@/views/base/DailyReceiving.vue");
const Search = () => import("@/views/base/Search.vue");
const FNSIDPreview = () => import("@/views/base/FNSIDPreview.vue");

Vue.use(Router);

export default new Router({
  mode: "hash", // https://router.vuejs.org/api/#mode
  linkActiveClass: "open active",
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      path: "/",
      redirect: "/dashboard",
      name: "Home",
      component: DefaultContainer,
      children: [
        {
          path: "dashboard",
          name: "Dashboard",
          component: Dashboard
        },
        {
          path: "fnsid-preview",
          name: "FNSID Preview",
          component: FNSIDPreview,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "waiting-for-parts",
          name: "Waiting For Parts",
          component: WaitingForParts
        },
        {
          path: "daily-receiving",
          name: "Daily Receiving",
          component: DailyReceiving
        },
        {
          path: "search",
          name: "Search Form",
          component: Search,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "base/delivery-proof",
          name: "Proof Of Delivery",
          component: ProofOfDelivery
        },
        {
          path: "base/company-profile",
          name: "Company Profile",
          component: CompanyProfile,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "/failure-details",
          name: "Failure Details",
          component: FailureDetails,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "base/TableDeliveredPerCustomer",
          name: "TableDeliveredPerCustomer",
          component: TableDeliveredPerCustomer,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "base/backlog-by-location",
          name: "Backlog By Location",
          component: BacklogByLocation
        },
        {
          path: "base/IssuesPreview",
          name: "Issues Preview",
          component: IssuePreview
        },
        {
          path: "/issue",
          name: "Issues Profile",
          component: IssueProfile,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "base/IssuesMap",
          name: "Map By Region",
          component: IssueMap
        },
        {
          path: "stackedChart",
          name: "stackedChart",
          component: stackedChart
        },
        {
          path: "analyticsByModel",
          name: "AnalyticsByModel",
          component: AnalyticsByModel
        },
        {
          path: "model",
          name: "Model",
          component: ModelView,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "theme",
          redirect: "/theme/colors",
          name: "Theme",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "colors",
              name: "Colors",
              component: Colors
            },
            {
              path: "typography",
              name: "Typography",
              component: Typography
            }
          ]
        },
        {
          path: "charts",
          name: "Charts",
          component: Charts
        },
        {
          path: "widgets",
          name: "Widgets",
          component: Widgets
        },
        {
          path: "users",
          meta: { label: "Users" },
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "",
              component: Users
            },
            {
              path: ":id",
              meta: { label: "User Details" },
              name: "User",
              component: User
            }
          ]
        },
        {
          path: "base",
          redirect: "/base/cards",
          name: "Base",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "profile",
              name: "Profile Settings",
              component: Profile
            },
            {
              path: "cards",
              name: "Cards",
              component: Cards
            },
            {
              path: "forms",
              name: "Forms",
              component: Forms
            },
            {
              path: "SubDivision",
              name: "Sub Division",
              component: SubDivision
            },
            {
              path: "BER",
              name: "BER",
              component: BER
            },
            {
              path: "EOL",
              name: "EOL",
              component: EOL
            },
            {
              path: "NTF",
              name: "NTF",
              component: NTF
            },
            {
              path: "WAR",
              name: "WAR",
              component: WAR
            },
            {
              path: "TAT",
              name: "TAT",
              component: TAT
            },
            {
              path: "OemRepair",
              name: "OEM",
              component: OEM
            },
            {
              path: "Reports",
              name: "Reports",
              component: Reports
            },
            {
              path: "fin-reports",
              name: "Financial Reports",
              component: ReportsFin
            },
            {
              path: "AddNewItem",
              name: "Add New Inventory Item",
              component: Forms
            },
            {
              path: "Inventory",
              name: "Repaired Inventory",
              component: Inventory
            },
            {
              path: "Backlog",
              name: "Active Backlog",
              component: Backlog
            },
            {
              path: "Map",
              name: "Map Overview",
              component: Map
            },
            {
              path: "SalesMap",
              name: "Sales Map",
              component: SalesMap
            },
            {
              path: "SalesRep",
              name: "Sales Rep",
              component: SalesRep
            },
            {
              path: "tables",
              name: "Tables",
              component: Tables,
              props(route) {
                return route.query || {};
              }
            },
            {
              path: "TableAllData",
              name: "TableAllData",
              component: TableAllData,
              props(route) {
                return route.query || {};
              }
            },
            {
              path: "city",
              name: "City",
              component: City,
              props(route) {
                return route.query || {};
              }
            },
            {
              path: "MapByState",
              name: "MapByState",
              component: MapByState,
              props(route) {
                return route.query || {};
              }
            },
            {
              path: "tabs",
              name: "Tabs",
              component: Tabs
            },
            {
              path: "breadcrumbs",
              name: "Breadcrumbs",
              component: Breadcrumbs
            },
            {
              path: "carousels",
              name: "Carousels",
              component: Carousels
            },
            {
              path: "collapses",
              name: "Collapses",
              component: Collapses
            },
            {
              path: "jumbotrons",
              name: "Jumbotrons",
              component: Jumbotrons
            },
            {
              path: "list-groups",
              name: "List Groups",
              component: ListGroups
            },
            {
              path: "navs",
              name: "Navs",
              component: Navs
            },
            {
              path: "navbars",
              name: "Navbars",
              component: Navbars
            },
            {
              path: "paginations",
              name: "Paginations",
              component: Paginations
            },
            {
              path: "popovers",
              name: "Popovers",
              component: Popovers
            },
            {
              path: "progress-bars",
              name: "Progress Bars",
              component: ProgressBars
            },
            {
              path: "tooltips",
              name: "Tooltips",
              component: Tooltips
            }
          ]
        },
        {
          path: "buttons",
          redirect: "/buttons/standard-buttons",
          name: "Buttons",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "standard-buttons",
              name: "Standard Buttons",
              component: StandardButtons
            },
            {
              path: "button-groups",
              name: "Button Groups",
              component: ButtonGroups
            },
            {
              path: "dropdowns",
              name: "Dropdowns",
              component: Dropdowns
            },
            {
              path: "brand-buttons",
              name: "Brand Buttons",
              component: BrandButtons
            }
          ]
        },
        {
          path: "icons",
          redirect: "/icons/font-awesome",
          name: "Icons",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "coreui-icons",
              name: "CoreUI Icons",
              component: CoreUIIcons
            },
            {
              path: "flags",
              name: "Flags",
              component: Flags
            },
            {
              path: "font-awesome",
              name: "Font Awesome",
              component: FontAwesome
            },
            {
              path: "simple-line-icons",
              name: "Simple Line Icons",
              component: SimpleLineIcons
            }
          ]
        },
        {
          path: "notifications",
          redirect: "/notifications/alerts",
          name: "Notifications",
          component: {
            render(c) {
              return c("router-view");
            }
          },
          children: [
            {
              path: "alerts",
              name: "Alerts",
              component: Alerts
            },
            {
              path: "badges",
              name: "Badges",
              component: Badges
            },
            {
              path: "modals",
              name: "Modals",
              component: Modals
            }
          ]
        }
      ]
    },
    {
      path: "/pages",
      redirect: "/pages/404",
      name: "Pages",
      component: {
        render(c) {
          return c("router-view");
        }
      },
      children: [
        {
          path: "404",
          name: "Page404",
          component: Page404
        },
        {
          path: "500",
          name: "Page500",
          component: Page500
        },
        {
          path: "login",
          name: "Login",
          component: Login,
          props(route) {
            return route.query || {};
          }
        },
        {
          path: "register",
          name: "Register",
          component: Register
        }
      ]
    }
  ]
});
